import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiUserSwitchBold } from 'react-icons/pi';
import { Button, Input, Text } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import { invalidateDealCommissionQueries } from './invalidateDealCommissionQueries';

type SellerLinkState = {
    seller: { id: number; name: string; ploomes_id: number | null } | null;
    source: string | null;
    hub_override: boolean;
};

type UserOption = {
    id: number;
    name: string;
    email?: string | null;
    ploomes_id?: number | null;
};

type Props = {
    open: boolean;
    onClose: () => void;
    dealId: number | string;
};

export default function DealSellerLinkModal({ open, onClose, dealId }: Props) {
    const { t } = useI18n();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selected, setSelected] = useState<UserOption | null>(null);
    const [reason, setReason] = useState('');

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        if (!open) {
            setSearch('');
            setDebouncedSearch('');
            setSelected(null);
            setReason('');
        }
    }, [open]);

    const linkQuery = useQuery({
        queryKey: ['commission-seller-link', dealId],
        queryFn: async () => {
            const { data } = await api.get<{ data: SellerLinkState }>(
                `/commissioning/deals/${dealId}/seller-link`,
            );
            return data.data;
        },
        enabled: open && Boolean(dealId),
        staleTime: 15_000,
    });

    useEffect(() => {
        const current = linkQuery.data?.seller;
        if (!current) {
            setSelected(null);
            return;
        }
        setSelected({
            id: current.id,
            name: current.name,
            ploomes_id: current.ploomes_id,
        });
    }, [linkQuery.data]);

    const usersQuery = useQuery({
        queryKey: ['users-search-seller', debouncedSearch],
        queryFn: async () => {
            const { data } = await api.get<{ data: UserOption[] }>('/users', {
                params: {
                    search: debouncedSearch || undefined,
                    per_page: 20,
                },
            });
            return data.data ?? [];
        },
        enabled: open && debouncedSearch.length >= 2,
        staleTime: 30_000,
    });

    const saveMutation = useMutation({
        mutationFn: async () => {
            if (!selected?.id) {
                throw new Error('missing_seller');
            }
            const { data } = await api.put(`/commissioning/deals/${dealId}/seller-link`, {
                seller_user_id: selected.id,
                reason: reason.trim() || null,
            });
            return data;
        },
        onSuccess: async () => {
            toast.success(t('v2.ploomes_deals.seller_link.toast_ok'));
            await invalidateDealCommissionQueries(queryClient, dealId);
            await queryClient.invalidateQueries({ queryKey: ['commission-seller-link', dealId] });
            onClose();
        },
        onError: (err: unknown) => {
            const msg =
                (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
                    ?.response?.data?.errors
                    ? Object.values(
                          (err as { response: { data: { errors: Record<string, string[]> } } }).response.data
                              .errors,
                      ).flat()[0]
                    : (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                      ?? t('v2.ploomes_deals.seller_link.toast_error');
            toast.error(msg);
        },
    });

    const current = linkQuery.data?.seller;
    const canSave = Boolean(selected?.id) && selected?.id !== current?.id && !saveMutation.isPending;

    return (
        <V2Modal open={open} onClose={onClose} title={t('v2.ploomes_deals.seller_link.title')} size="md">
            <div className="space-y-4">
                <Text className="text-sm text-gray-600 dark:text-zinc-400">
                    {t('v2.ploomes_deals.seller_link.description')}
                </Text>

                {current && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-950 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-100">
                        {t('v2.ploomes_deals.seller_link.current', { name: current.name })}
                    </div>
                )}

                <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
                        {t('v2.ploomes_deals.seller_link.seller')}
                    </label>
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('v2.ploomes_deals.seller_link.search_placeholder')}
                    />
                    {selected && (
                        <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2 text-xs dark:border-zinc-700 dark:bg-zinc-800/60">
                            <span className="font-medium text-gray-900 dark:text-zinc-100">{selected.name}</span>
                            <button
                                type="button"
                                className="text-gray-500 hover:text-gray-800 dark:hover:text-zinc-200"
                                onClick={() => setSelected(null)}
                            >
                                {t('v2.ploomes_deals.seller_link.clear_selection')}
                            </button>
                        </div>
                    )}
                    {debouncedSearch.length >= 2 && (
                        <ul className="max-h-40 overflow-auto rounded-md border border-gray-200 dark:border-zinc-700">
                            {(usersQuery.data ?? []).map((user) => (
                                <li key={user.id}>
                                    <button
                                        type="button"
                                        className={cn(
                                            'flex w-full flex-col items-start px-2.5 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-zinc-800',
                                            selected?.id === user.id && 'bg-sky-50 dark:bg-sky-950/40',
                                        )}
                                        onClick={() => {
                                            setSelected(user);
                                            setSearch(user.name);
                                        }}
                                    >
                                        <span className="font-medium text-gray-900 dark:text-zinc-100">{user.name}</span>
                                        {user.email && (
                                            <span className="text-[10px] text-gray-500">{user.email}</span>
                                        )}
                                    </button>
                                </li>
                            ))}
                            {usersQuery.isFetched && (usersQuery.data ?? []).length === 0 && (
                                <li className="px-2.5 py-2 text-xs text-gray-500">
                                    {t('v2.ploomes_deals.seller_link.no_results')}
                                </li>
                            )}
                        </ul>
                    )}
                </div>

                <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
                        {t('v2.ploomes_deals.seller_link.reason')}
                    </label>
                    <Input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t('v2.ploomes_deals.seller_link.reason_placeholder')}
                    />
                </div>

                <Text className="text-[11px] text-gray-500 dark:text-zinc-400">
                    {t('v2.ploomes_deals.seller_link.hint')}
                </Text>

                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-3 dark:border-zinc-800">
                    <Button type="button" size="sm" variant="outline" disabled={saveMutation.isPending} onClick={onClose}>
                        {t('v2.ploomes_deals.seller_link.cancel')}
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        isLoading={saveMutation.isPending}
                        disabled={!canSave}
                        onClick={() => saveMutation.mutate()}
                    >
                        <PiUserSwitchBold className="mr-1 h-3.5 w-3.5" />
                        {t('v2.ploomes_deals.seller_link.save')}
                    </Button>
                </div>
            </div>
        </V2Modal>
    );
}
